import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Table,
  Button,
  Input,
  Pagination,
  Space,
  Modal,
  message,
  Dropdown,
  Form,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  UserAddOutlined,
  PlusOutlined,
  DownOutlined,
  UserDeleteOutlined,
} from "@ant-design/icons";
import slugify from "slugify";
import "antd/dist/reset.css";
import '../../styles/Categories.css';
import CategoryApiService from '../../services/CategoryApiService';
import { useDebounceSearch } from '../../utils/debounce';
import {
  generateSlug,
  isValidSlug,
  hasLeadingOrTrailingSpaces,
  isValidSlugLength,
} from '../../utils/SlugValidate';
import {
  PAGINATION_DEFAULT,
  DEBOUNCE_TIMES,
  VALIDATION_MESSAGES,
  TABLE_COLUMNS,
} from '../../constants/categories';
import AddCategoryModal from '../../components/categories/AddCategoryModal';
import EditCategoryModal from '../../components/categories/EditCategoryModal';
import AssignCategoryModal from '../../components/categories/AssignCategoryModal';

const { Search } = Input;

const Categories = () => {
  // State management
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    current: PAGINATION_DEFAULT.DEFAULT_PAGE,
    pageSize: PAGINATION_DEFAULT.DEFAULT_PAGE_SIZE,
    total: 0,
  });

  // Ref to track initial mount
  const isInitialMount = useRef(true);

  // Modal states
  const [isAssignModalVisible, setIsAssignModalVisible] = useState(false);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);

  // Form states
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryDescription, setNewCategoryDescription] = useState("");
  const [newCategorySlug, setNewCategorySlug] = useState("");
  const [assignUsers, setAssignUsers] = useState([]);
  const [assignUsersLoading, setAssignUsersLoading] = useState(false);
  const [assignUsersSearch, setAssignUsersSearch] = useState('');
  const [selectedAssignUsers, setSelectedAssignUsers] = useState([]);
  const [confirmLoading, setConfirmLoading] = useState(false);
  // Validation states
  const [nameStatus, setNameStatus] = useState("");
  const [nameHelp, setNameHelp] = useState("");
  const [slugStatus, setSlugStatus] = useState("");
  const [slugHelp, setSlugHelp] = useState("");
  const [descriptionStatus, setDescriptionStatus] = useState("");
  const [descriptionHelp, setDescriptionHelp] = useState("");

  // Refs for debouncing
  const nameDebounceRef = useRef();
  const slugDebounceRef = useRef();

  // API calls
  const fetchCategories = useCallback(
    async (page, pageSize, search) => {
      setLoading(true);
      try {
        const res = await CategoryApiService.getCategories({
          page,
          pageSize,
          search,
          sort: "-createdAt"
        });
        const categories = res.categories.map((cat) => ({
          key: cat._id,
          name: cat.name,
          slug: cat.slug,
          description: cat.description,
          assignedUsers: cat.assignedUsers,
          createdAt: cat.createdAt,
          pagination: {
            current: res.pagination.currentPage,
            pageSize: res.pagination.itemsPerPage
          }
        }));

        // Sort categories by createdAt in descending order (newest first)
        const sortedCategories = categories.sort((a, b) => {
          if (!a.createdAt || !b.createdAt) return 0;
          return new Date(b.createdAt) - new Date(a.createdAt);
        });

        setData(sortedCategories);
        setPagination({ 
          current: res.pagination.currentPage,
          pageSize: res.pagination.itemsPerPage,
          total: res.pagination.totalItems,
        });
      } catch (err) {
        message.error("Failed to fetch categories");
      }
      setLoading(false);
    },
    [setData, setPagination, setLoading]
  );

  const fetchAssignableUsers = useCallback(async (searchText = '') => {
    if (!isAssignModalVisible) return;
    
    setAssignUsersLoading(true);
    try {
      const response = await CategoryApiService.assignableUsers({
        search: searchText,
        limit: 100,
        page: 1
      });
      const users = response.data?.users || [];
      setAssignUsers(users);
    } catch (error) {
      console.error("Error fetching assignable users:", error);
      message.error("Failed to fetch assignable users");
    } finally {
      setAssignUsersLoading(false);
    }
  }, [isAssignModalVisible, setAssignUsers, setAssignUsersLoading]);

  // Helper to get latest pagination state for debounced search
  const getLatestPagination = useCallback(() => pagination, [pagination]);

  // Use debounce hook for main search
  const { debouncedSearch, clearSearchTimeout } = useDebounceSearch((value, latestParams) => {
    const trimmedValue = value.trim();
    // Reset to page 1 for new search
    setPagination(prev => ({ ...prev, current: 1 })); // Ensure pagination is reset before fetching
    fetchCategories(1, latestParams.pageSize, trimmedValue);
  }, 500, getLatestPagination);

  // Use another debounce hook instance for assign users search
  const { debouncedSearch: debouncedAssignSearch } = useDebounceSearch((value) => {
    fetchAssignableUsers(value);
  });

  // Event handlers
  const handleSearch = useCallback((value) => {
    clearSearchTimeout(); // Clear any pending debounced search
    const trimmedValue = value.trim();
    setSearchText(value); 
    // Always reset to page 1 for direct search via button
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchCategories(1, pagination.pageSize, trimmedValue); 
  }, [fetchCategories, pagination.pageSize, clearSearchTimeout]);

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchText(value); 
    // Only trigger debounced search when typing
    debouncedSearch(value);
  }, [debouncedSearch]);

  const handleTableChange = useCallback(
    (pag) => {
      // Update pagination state first, then fetch
      setPagination(pag);
      fetchCategories(pag.current, pag.pageSize, searchText);
    },
    [fetchCategories, searchText] // searchText is a dependency here for direct fetch
  );

  // Initial fetch on component mount. Runs only once.
  useEffect(() => {
    fetchCategories(PAGINATION_DEFAULT.DEFAULT_PAGE, PAGINATION_DEFAULT.DEFAULT_PAGE_SIZE, "");
  }, [fetchCategories]); // fetchCategories is stable, so this runs once on mount.

  // Event handlers
  const handleCategoryNameChange = useCallback(
    (e) => {
      const value = e.target.value;
      setNewCategoryName(value);

      // Check name length and show error if exceeded
      if (value.length > 255) {
        setNameStatus("error");
        setNameHelp("Category name cannot exceed 255 characters");
      }

      // Always auto-generate slug from name if not in edit mode
      if (!isEditModalVisible) {
        const generatedSlug = generateSlug(value);
        console.log('Generated slug:', generatedSlug); // For debugging
        setNewCategorySlug(generatedSlug);
        
        // Update slug validation status
        if (!generatedSlug) {
          setSlugStatus("error");
          setSlugHelp("Slug cannot be empty");
        } else if (generatedSlug.length > 255) {
          setSlugStatus("error");
          setSlugHelp("URL-Friendly name cannot exceed 255 characters");
        } else if (!isValidSlugLength(generatedSlug)) {
          setSlugStatus("error");
          setSlugHelp("Slug must be at least 3 characters");
        } else if (!isValidSlug(generatedSlug)) {
          setSlugStatus("error");
          setSlugHelp("Invalid slug format");
        } else {
          const exists = data.some((item) => item.slug === generatedSlug);
          if (exists) {
            setSlugStatus("error");
            setSlugHelp(VALIDATION_MESSAGES.SLUG_EXISTS);
          } else {
            setSlugStatus("success");
            setSlugHelp(VALIDATION_MESSAGES.SLUG_SUCCESS);
          }
        }
      }

      if (nameDebounceRef.current) clearTimeout(nameDebounceRef.current);
      nameDebounceRef.current = setTimeout(async () => {
        if (!value.trim()) {
          setNameStatus("error");
          setNameHelp(VALIDATION_MESSAGES.NAME_EMPTY);
          return;
        }

        // Check minimum length
        if (value.trim().length < 3) {
          setNameStatus("error");
          setNameHelp(VALIDATION_MESSAGES.NAME_INVALID_LENGTH);
          return;
        }

        try {
          const exists = data.some(
            (item) =>
              item.name.trim().toLowerCase() === value.trim().toLowerCase() &&
              (!selectedCategory || item.key !== selectedCategory.key) // Only check for duplicates excluding current category in edit mode
          );
          if (exists) {
            setNameStatus("error");
            setNameHelp(VALIDATION_MESSAGES.NAME_EXISTS);
          } else if (value.length > 255) {
            setNameStatus("error");
            setNameHelp("Category name cannot exceed 255 characters");
          } else {
            setNameStatus("success");
            setNameHelp(VALIDATION_MESSAGES.NAME_SUCCESS);
          }
        } catch {
          setNameStatus("error");
          setNameHelp(VALIDATION_MESSAGES.NAME_ERROR);
        }
      }, DEBOUNCE_TIMES.NAME);
    },
    [data, isEditModalVisible, selectedCategory]
  );

  // Add new handler for description changes
  const handleDescriptionChange = useCallback((e) => {
    const value = e.target.value;
    setNewCategoryDescription(value);

    // Check description length
    if (value.length > 500) {
      setDescriptionStatus("error");
      setDescriptionHelp("Description cannot exceed 500 characters");
    } else {
      setDescriptionStatus("");
      setDescriptionHelp("");
    }
  }, []);

  const handleSlugChange = useCallback(
    (e) => {
      const value = e.target.value.toLowerCase();

      // Check for leading/trailing spaces first
      if (hasLeadingOrTrailingSpaces(value)) {
        setSlugStatus("error");
        setSlugHelp("Slug cannot contain leading or trailing spaces");
        return;
      }

      // Cho phép nhập trực tiếp, chỉ lọc ký tự không hợp lệ
      const filteredValue = value.replace(/[^a-z0-9-]/g, "");
      setNewCategorySlug(filteredValue);

      if (slugDebounceRef.current) clearTimeout(slugDebounceRef.current);
      slugDebounceRef.current = setTimeout(() => {
        // Basic slug validation
        if (!filteredValue) {
          setSlugStatus("error");
          setSlugHelp("Slug cannot be empty");
          return;
        }

        if (!isValidSlugLength(filteredValue)) {
          setSlugStatus("error");
          setSlugHelp("Slug must be at least 3 characters");
          return;
        }

        // Check for special characters
        if (!isValidSlug(filteredValue)) {
          setSlugStatus("error");
          setSlugHelp(
            "Slugs can only contain lowercase letters, numbers and hyphens"
          );
          return;
        }

        const exists = data.some((item) => item.slug === filteredValue);
        if (exists) {
          setSlugStatus("error");
          setSlugHelp(VALIDATION_MESSAGES.SLUG_EXISTS);
        } else {
          setSlugStatus("success");
          setSlugHelp(VALIDATION_MESSAGES.SLUG_SUCCESS);
        }
      }, DEBOUNCE_TIMES.SLUG);
    },
    [data]
  );

  // CRUD operations
  const handleAddCategory = useCallback(async () => {
    // Reset validation states first
    setNameStatus("");
    setNameHelp("");
    setSlugStatus("");
    setSlugHelp("");

    let hasError = false;

    // Validate category name
    if (!newCategoryName) {
      setNameStatus("error");
      setNameHelp("Category name is required");
      hasError = true;
    } else if (newCategoryName.length > 255) {
      setNameStatus("error");
      setNameHelp("Category name cannot exceed 255 characters");
      hasError = true;
    } else if (newCategoryName.trim().length < 3) {
      setNameStatus("error");
      setNameHelp(VALIDATION_MESSAGES.NAME_INVALID_LENGTH);
      hasError = true;
    } else {
      // Check if category name already exists
      const nameExists = data.some(
        (item) => item.name.trim().toLowerCase() === newCategoryName.trim().toLowerCase()
      );
      if (nameExists) {
        setNameStatus("error");
        setNameHelp(VALIDATION_MESSAGES.NAME_EXISTS);
        hasError = true;
      }
    }

    // Validate slug
    if (!newCategorySlug) {
      setSlugStatus("error");
      setSlugHelp("URL-Friendly name is required");
      hasError = true;
    } else if (newCategorySlug.length > 255) {
      setSlugStatus("error");
      setSlugHelp("URL-Friendly name cannot exceed 255 characters");
      hasError = true;
    } else if (!isValidSlugLength(newCategorySlug)) {
      setSlugStatus("error");
      setSlugHelp("URL-Friendly name must be at least 3 characters long");
      hasError = true;
    } else if (!isValidSlug(newCategorySlug)) {
      setSlugStatus("error");
      setSlugHelp("Invalid URL-Friendly format - Use only lowercase letters, numbers and hyphens");
      hasError = true;
    } else {
      // Check if slug already exists
      const slugExists = data.some(
        (item) => item.slug === newCategorySlug
      );
      if (slugExists) {
        setSlugStatus("error");
        setSlugHelp(VALIDATION_MESSAGES.SLUG_EXISTS);
        hasError = true;
      }
    }

    if (hasError) {
      message.error("Failed to create category");
      return;
    }

    setConfirmLoading(true);
    try {
      const response = await CategoryApiService.createCategory({
        name: newCategoryName,
        slug: newCategorySlug,
        description: newCategoryDescription,
      });

      if (response && response.data) {
        const newCat = response.data;
        setData(prevData => [
          {
            key: newCat._id,
            name: newCat.name,
            slug: newCat.slug,
            description: newCat.description || "",
            assignedUsers: newCat.assignedUsers || [],
            createdAt: newCat.createdAt,
            pagination: prevData[0]?.pagination || pagination
          },
          ...prevData
        ]);
        setPagination(prev => ({ ...prev, total: prev.total + 1 }));
      } else {
        await fetchCategories(1, pagination.pageSize, "");
      }
      
      message.success("Category added successfully");
      setIsAddModalVisible(false);
      setNewCategoryName("");
      setNewCategorySlug("");
      setNewCategoryDescription("");
      // Reset validation states
      setNameStatus("");
      setNameHelp("");
      setSlugStatus("");
      setSlugHelp("");
      
    } catch (error) {
      console.error("Error adding category:", error);
      const errorMessage = error.response?.data?.message || 
                         error.response?.data?.error || 
                         error.message || 
                         "Failed to create category";
      message.error(errorMessage);
      
      if (errorMessage?.toLowerCase().includes("name")) {
        setNameStatus("error");
        setNameHelp(errorMessage);
      } else if (errorMessage?.toLowerCase().includes("slug")) {
        setSlugStatus("error");
        setSlugHelp(errorMessage);
      }
    } finally {
      setConfirmLoading(false);
    }
  }, [
    newCategoryName,
    newCategorySlug,
    newCategoryDescription,
    data,
    pagination.pageSize,
    fetchCategories,
    pagination
  ]);

  const handleEditCategory = useCallback(async () => {
    // Validate category name
    if (!newCategoryName) {
      message.error("Category name is required");
      return;
    }

    if (newCategoryName.trim().length < 3) {
      message.error("Category name must be at least 3 characters long");
      return;
    }

    // Check if category name already exists (excluding current category)
    const nameExists = data.some(
      (item) => 
        item.name.trim().toLowerCase() === newCategoryName.trim().toLowerCase() &&
        item.key !== selectedCategory.key
    );
    if (nameExists) {
      message.error("Category name already exists");
      return;
    }

    try {
      await CategoryApiService.updateCategory(selectedCategory.key, {
        name: newCategoryName,
        description: newCategoryDescription,
      });

      // Update the category in the current data array instead of refetching
      setData(prevData => {
        const updatedData = prevData.map(item => {
          if (item.key === selectedCategory.key) {
            return {
              ...item,
              name: newCategoryName,
              description: newCategoryDescription
            };
          }
          return item;
        });
        return updatedData;
      });

      message.success("Category updated successfully");
      setIsEditModalVisible(false);
      setSelectedCategory(null);
      setNewCategoryName("");
      setNewCategoryDescription("");
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                         error.response?.data?.error || 
                         error.message || 
                         "Failed to update category";
      message.error(errorMessage);
    }
  }, [
    selectedCategory,
    newCategoryName,
    newCategoryDescription,
    data
  ]);

  const handleDelete = useCallback(
    (record) => {
      Modal.confirm({
        title: "Are you sure you want to delete this category?",
        content: record.name,
        okText: "Yes",
        okType: "danger",
        cancelText: "No",
        onOk: async () => {
          try {
            await CategoryApiService.deleteCategory(record.key);
            message.success("Category deleted successfully");
            fetchCategories(
              pagination.current,
              pagination.pageSize,
              searchText
            );
          } catch (error) {
            // Show specific error message from server
            const errorMessage = error.response?.data?.message || "Failed to delete category";
            message.error(errorMessage);
          }
        },
      });
    },
    [fetchCategories, pagination, searchText]
  );

  //assign users to category  
  const handleAssignUsers = useCallback(async (updatedUsers) => {
    try {
      setConfirmLoading(true);
      
      // Update local state immediately
      setData(prevData => 
        prevData.map(item => 
          item.key === selectedCategory.key 
            ? { ...item, assignedUsers: updatedUsers }
            : item
        )
      );

      // Close modal without showing duplicate message
      setIsAssignModalVisible(false);
      setSelectedCategory(null);
    } catch (error) {
      console.error("Error updating users:", error);
      message.error(error.response?.data?.message || "Failed to update users");
    } finally {
      setConfirmLoading(false);
    }
  }, [selectedCategory]);

  // Add this new function to handle unassigning users
  const handleUnassignUsers = useCallback(async (record) => {
    Modal.confirm({
      title: "Are you sure you want to unassign all users from this category?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await CategoryApiService.unassignCategory(record.key);
          
          // Update the local state
          setData(prevData => 
            prevData.map(item => 
              item.key === record.key 
                ? { ...item, assignedUsers: [] }
                : item
            )
          );
          
          message.success("Users unassigned successfully");
        } catch (error) {
          console.error("Error unassigning users:", error);
          message.error("Failed to unassign users");
        }
      },
    });
  }, []);

  // Update handleAssignUsersSearch to use the new debounce hook
  const handleAssignUsersSearch = useCallback((value) => {
    setAssignUsersSearch(value);
    debouncedAssignSearch(value);
  }, [debouncedAssignSearch]);

  // Reset form when opening Add modal
  useEffect(() => {
    if (isAddModalVisible) {
      setNewCategoryName("");
      setNewCategorySlug("");
      setNewCategoryDescription("");
      setSlugStatus("");
      setSlugHelp("");
      setNameStatus("");
      setNameHelp("");
    }
  }, [isAddModalVisible]);

  // Reset all states when closing any modal
  const resetValidationStates = useCallback(() => {
    setNameStatus("");
    setNameHelp("");
    setSlugStatus("");
    setSlugHelp("");
    setDescriptionStatus("");
    setDescriptionHelp("");
  }, []);

  // Update modal visibility handlers
  const handleModalCancel = useCallback(() => {
    setIsAddModalVisible(false);
    setIsEditModalVisible(false);
    setIsAssignModalVisible(false);
    setSelectedCategory(null);
    setNewCategoryName("");
    setNewCategorySlug("");
    setNewCategoryDescription("");
    resetValidationStates();
  }, [resetValidationStates]);

  // Effect to reset validation states when modals are closed
  useEffect(() => {
    if (!isAddModalVisible && !isEditModalVisible) {
      resetValidationStates();
    }
  }, [isAddModalVisible, isEditModalVisible, resetValidationStates]);

  // Memoized values
  const actionColumns = useMemo(() => [{
    title: "Operation",
    key: "operation",
    align: "center",
    render: (_, record) => (
      <Dropdown
        menu={{
          items: [
            {
              key: "edit",
              icon: <EditOutlined />,
              label: "Edit",
              onClick: () => {
                setSelectedCategory(record);
                setNewCategoryName(record.name);
                setNewCategoryDescription(record.description || "");
                setIsEditModalVisible(true);
              },
            },
            {
              key: "delete",
              icon: <DeleteOutlined />,
              label: "Delete",
              onClick: () => handleDelete(record),
            },
            {
              key: "assign",
              icon: <UserAddOutlined />,
              label: "Assign Users",
              onClick: () => {
                setSelectedCategory(record);
                const assignedUserIds = Array.isArray(record.assignedUsers)
                  ? record.assignedUsers.map(u => typeof u === "string" ? u : u._id)
                  : [];
                setSelectedAssignUsers(assignedUserIds);
                setIsAssignModalVisible(true);
              },
            }
          ],
        }}
        trigger={["click"]}
      >
        <Button>
          Action <DownOutlined />
        </Button>
      </Dropdown>
    ),
  }], [handleDelete]);

  const columns = useMemo(
    () => [...TABLE_COLUMNS, ...actionColumns],
    [actionColumns]
  );

  // Effect to fetch users when modal becomes visible
  useEffect(() => {
    if (isAssignModalVisible) {
      // Only fetch on modal open, the debounced search will handle subsequent searches
      fetchAssignableUsers(assignUsersSearch);
    }
  }, [isAssignModalVisible, fetchAssignableUsers]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearSearchTimeout();
    };
  }, [clearSearchTimeout]);

  return (
    <div className="categories-container">
      <h1 className="admin-title">Categories Management</h1>

      <div className="categories-top-bar">
        <Search
          placeholder="Search content.."
          value={searchText}
          onChange={handleSearchChange}
          onSearch={handleSearch}
          style={{ maxWidth: 500 }}
        />
        <div className="categories-top-bar-buttons">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsAddModalVisible(true)}
            className="add-btnw"
          >
            Add Category
          </Button>
        </div>
      </div>

      <Table
        className="striped-table"
        columns={columns}
        dataSource={data}
        pagination={false}
        loading={loading}
        bordered
      />

      <div className="categories-pagination">
        <Pagination
          current={pagination.current}
          total={pagination.total}
          pageSize={pagination.pageSize}
          showTotal={(total) => `Total ${total} categories`}
          onChange={(page, pageSize) =>
            fetchCategories(page, pageSize, searchText)
          }
        />
      </div>

      <AddCategoryModal
        visible={isAddModalVisible}
        onCancel={handleModalCancel}
        onOk={handleAddCategory}
        nameStatus={nameStatus}
        nameHelp={nameHelp}
        slugStatus={slugStatus}
        slugHelp={slugHelp}
        categoryName={newCategoryName}
        categorySlug={newCategorySlug}
        categoryDescription={newCategoryDescription}
        onNameChange={handleCategoryNameChange}
        onSlugChange={handleSlugChange}
        onDescriptionChange={handleDescriptionChange}
        descriptionStatus={descriptionStatus}
        descriptionHelp={descriptionHelp}
      />

      <EditCategoryModal
        visible={isEditModalVisible}
        onCancel={handleModalCancel}
        onOk={handleEditCategory}
        categoryName={newCategoryName}
        categorySlug={selectedCategory?.slug || ""}
        categoryDescription={newCategoryDescription}
        onNameChange={handleCategoryNameChange}
        onDescriptionChange={handleDescriptionChange}
        nameStatus={nameStatus}
        nameHelp={nameHelp}
      />

      <AssignCategoryModal
        visible={isAssignModalVisible}
        onCancel={() => {
          setIsAssignModalVisible(false);
          setSelectedCategory(null);
          setAssignUsersSearch('');
        }}
        onOk={handleAssignUsers}
        confirmLoading={confirmLoading}
        categoryId={selectedCategory?.key}
        currentAssignedUsers={selectedCategory?.assignedUsers || []}
        users={assignUsers}
        onSearch={handleAssignUsersSearch}
        loading={assignUsersLoading}
      />
    </div>
  );
};

export default Categories;
